// Read more: https://kit.svelte.dev/docs/load
import { PREVIEW_MODE } from '$lib/global/constants';

import { preloadBlocks, ALL_BLOCK_TYPES } from '$lib/components/Blocks/map';
import { themeBorderRadius, embedOptions, advancedLinking } from '$lib/Stores/stores';
import { appendDomainQueryParam } from '$lib/utils/appendDomainQueryParam';
import { getDataBaseUrl } from '$lib/utils/getDataBaseUrl';
import { isFunBrandsEnabled } from '$lib/utils/isFunBrandsEnabled';
import { loadFontsAsync } from '$lib/utils/previewUtils';

import debug from 'debug';

import type { LayoutData } from '$lib/types/LayoutData';
import type { Theme } from '$lib/types/Theme';
import type { LayoutLoad } from './$types';

export const prerender = false;
export const ssr = false;
export const trailingSlash = 'always';

const debugLog = debug('routes:+layout.js');

const loadCustomFont = async (fontId: string | undefined, companyId: string): Promise<void> => {
    if (!fontId) return;
    await loadFontsAsync(
        `https://assets.perspective.co/ps-assets/${companyId}/fonts/${fontId}.css`,
    );

    try {
        let customFonts: FontFace[] = [];

        document.fonts.ready.then((fontFaceSet) => {
            const fontFaces = [...fontFaceSet];
            customFonts = fontFaces.filter((font) => font.family === fontId);
        });

        await Promise.all(customFonts.map((font) => font.load()));
    } catch (error) {
        console.error('Custom font failed to load', error);
    }
};

export const load: LayoutLoad = async ({ fetch }) => {
    const dataBasePath = getDataBaseUrl();

    // window.__BLOCK_TYPES__ is injected by the funnel-publisher at publish time.
    // Falls back to loading ALL blocks when not present (backwards compatible).
    const blockTypes: string[] = (window as any).__BLOCK_TYPES__ || ALL_BLOCK_TYPES;

    // Preload block components and fetch layout data in parallel.
    // When modulepreload tags are present, the import() calls resolve
    // from the browser's preload cache almost instantly.
    const [layoutData] = await Promise.all([
        fetch(
            `${dataBasePath}/layoutData.json?v=${
                window.campaignVersionId
            }${appendDomainQueryParam()}`,
        ).then((res) => res.json() as Promise<LayoutData>),
        preloadBlocks(blockTypes),
    ]);

    // TODO: Remove once Theming 2.0 is fully migrated
    if (isFunBrandsEnabled(layoutData)) {
        layoutData.theme = layoutData.themeV2;
        layoutData.theme.transition = layoutData.themeV2.pageTransition;
    }

    // Populate stores
    themeBorderRadius.set(layoutData.theme.borderRadius);
    advancedLinking.set(layoutData.advancedLinkingLogic || {});

    const urlParams = new URLSearchParams(window.location.search);

    embedOptions.set({
        isEmbed: urlParams.get('embed') === '1',
        // if preview is true, we don't send analytics data
        preview: PREVIEW_MODE ? true : urlParams.get('preview') === '1',
        header: urlParams.get('header') !== '0',
        footer: urlParams.get('footer') !== '0',
        autoHeight: urlParams.get('autoHeight') === '1',
        cookieBanner: urlParams.get('cookieBanner') !== '0',
    });

    debugLog(layoutData);

    // load custom fonts in preview mode (published funnels get font links injected at build time)
    if (PREVIEW_MODE) {
        const { fontId, headingFontId } = layoutData.theme as Theme;
        await Promise.all([
            loadCustomFont(fontId, layoutData.companyId),
            loadCustomFont(headingFontId, layoutData.companyId),
        ]);
    }

    return layoutData;
};
